import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { groupBy, maxBy, minBy, sortBy, uniqBy } from 'lodash'
import moment from 'moment'

import { encodeOccurrence } from '../../../helpers/encodeOccurrence'
import { BulkEventTableRow, OpportunityEvent, OpportunityRegistrations, OpportunityTimeshift } from '../types'

dayjs.extend(utc)
dayjs.extend(timezone)

const emptyRegistrations: OpportunityRegistrations = {
  total: 0,
  confirmed: 0,
  available: 0,
}

const getTimeZone = (timeZone?: string) => timeZone || moment.tz.guess()

const getEncodedOccurrenceForEvent = (event: OpportunityEvent): string | null => {
  if (!event.opportunityId) {
    return null
  }

  const timeZone = getTimeZone(event.organization?.timeZone)
  const dateString = moment.tz(event.startsAt, timeZone).format('YYYY-MM-DD')

  return encodeOccurrence(event.opportunityId, dateString, timeZone)
}

const getShiftDate = (eventDate: string | Date, shiftTime: string, timeZone?: string): Date => {
  const tz = timeZone || dayjs.tz.guess()
  const eventStringDate = dayjs(eventDate).format('YYYY-MM-DD')
  const timeMatch = shiftTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)

  if (!timeMatch) {
    return new Date(eventDate)
  }

  let hours = parseInt(timeMatch[1], 10)
  const minutes = parseInt(timeMatch[2], 10)
  const ampm = timeMatch[3].toUpperCase()

  if (ampm === 'PM' && hours !== 12) {
    hours += 12
  } else if (ampm === 'AM' && hours === 12) {
    hours = 0
  }

  return dayjs.tz(eventStringDate, tz).hour(hours).minute(minutes).second(0).millisecond(0).toDate()
}

const formatTimeLabel = (date: Date | undefined, timeZone?: string): string => {
  if (!date) {
    return '—'
  }

  return moment(date).tz(getTimeZone(timeZone)).format('h:mm A')
}

const buildShiftRoleRowKey = ({
  encodedOccurrence,
  shiftId,
  roleId,
  roleName,
}: {
  encodedOccurrence: string
  shiftId: number
  roleId: number
  roleName: string
}): string => `${encodedOccurrence}-${shiftId}-${roleId}-${roleName}`

const mapOpportunityToShiftRoleRows = (event: OpportunityEvent): BulkEventTableRow[] => {
  const { id, occurrenceId, opportunityId, startsAt, name, timeshifts, organization, missingRequiredMemberships } =
    event
  const encodedOccurrence = getEncodedOccurrenceForEvent(event)

  if (!encodedOccurrence) {
    return []
  }

  if (!(timeshifts || []).length) {
    return [
      {
        id,
        rowKey: encodedOccurrence,
        occurrenceId,
        opportunityId,
        startsAt: new Date(startsAt),
        endsAt: event.endsAt ? new Date(event.endsAt) : undefined,
        eventStartsAt: startsAt,
        name,
        organization,
        missingRequiredMemberships,
        registrations: emptyRegistrations,
      },
    ]
  }

  return (timeshifts as OpportunityTimeshift[]).flatMap(shift =>
    shift.roles.map(role => {
      const shiftStartsAt = getShiftDate(startsAt, shift.time_start, organization?.timeZone)
      const shiftEndsAt = getShiftDate(startsAt, shift.time_end, organization?.timeZone)

      return {
        id,
        rowKey: buildShiftRoleRowKey({
          encodedOccurrence,
          shiftId: shift.id,
          roleId: role.id,
          roleName: role.name,
        }),
        encodedOccurrence,
        occurrenceId,
        opportunityId,
        startsAt: shiftStartsAt,
        endsAt: shiftEndsAt,
        eventStartsAt: startsAt,
        startTimeLabel: formatTimeLabel(shiftStartsAt, organization?.timeZone),
        endTimeLabel: formatTimeLabel(shiftEndsAt, organization?.timeZone),
        name,
        organization,
        roleId: role.id,
        roleName: role.name,
        shiftName: shift.name,
        shiftId: shift.id,
        missingRequiredMemberships,
        registrations: {
          total: role.all,
          confirmed: role.all - role.available,
          available: role.available,
        },
      }
    }),
  )
}

export const mapDailyEvents = (events: OpportunityEvent[]): BulkEventTableRow[] => {
  const uniqueEvents = uniqBy(events, event => getEncodedOccurrenceForEvent(event) ?? `event-${event.id}`)
  const rows = uniqBy(uniqueEvents.flatMap(mapOpportunityToShiftRoleRows), 'rowKey')
  const groupedRows = groupBy(rows, ({ startsAt }) => moment(startsAt).format('YYYY-MM-DD'))

  return sortBy(
    Object.entries(groupedRows).map(([date, dayRows]) => ({
      id: parseInt(date.replace(/-/g, ''), 10),
      rowKey: `day-${date}`,
      name: moment(date).format('dddd, MMMM Do, YYYY'),
      startsAt: (minBy(dayRows, 'startsAt') as BulkEventTableRow).startsAt,
      endsAt: (maxBy(dayRows, 'endsAt') as BulkEventTableRow).endsAt,
      isDayGroup: true,
      registrations: dayRows.reduce(
        (registrations, row) => ({
          total: registrations.total + row.registrations.total,
          confirmed: registrations.confirmed + row.registrations.confirmed,
          available: registrations.available + row.registrations.available,
        }),
        emptyRegistrations,
      ),
      subRows: sortBy(dayRows, ['startsAt', 'name', 'roleName']),
    })),
    'startsAt',
  )
}

export const encodeOccurrenceFromTableRow = (row: BulkEventTableRow): string => {
  if (row.encodedOccurrence) {
    return row.encodedOccurrence
  }

  const timeZone = getTimeZone(row.organization?.timeZone)
  const dateString = moment.tz(row.eventStartsAt, timeZone).format('YYYY-MM-DD')
  return encodeOccurrence(row.opportunityId as number, dateString, timeZone)
}

export const isRowSelectable = (row: BulkEventTableRow): boolean =>
  !row.isDayGroup && !row.missingRequiredMemberships && row.registrations.available > 0 && !!row.roleId && !!row.shiftId
